/**
 * Maps backend dropdown key values to their human-readable Italian labels.
 * Used by the comparison dialog to display meaningful values instead of raw keys.
 */

/** Boolean / Yes-No fields */
const BOOLEAN_LABELS: Record<string, string> = {
  true: 'Sì',
  false: 'No',
};

/** Fields whose values are boolean-like (true/false as strings) */
const BOOLEAN_FIELDS = new Set([
  'sito_web',
  'periodo_imposta',
  'manuale_conservazione',
  'intermediario_sdi',
  'servizio_trasmissione_agenzia_entrate',
  'servizio_conservatore_esterno',
  'obbligo_rentri',
  'servizio_conservazione',
  'servizio_conservazione_xfir',
  'servizio_conservazione_attivo',
  'dati_conservazione.atto_delega_nomina',
  'dati_conservazione.modalita_erogazione_servizio',
  'dati_conservazione.accesso_portale_conservazione',
  'dati_conservazione.conservazione_mensilmente',
  'dati_conservazione.notifiche_avvenuta_conservazione',
  'dati_conservazione.conservazione_anni_precedenti',
  'dati_conservazione_xfir.atto_delega_nomina',
  'dati_conservazione_xfir.modalita_erogazione_servizio',
  'dati_conservazione_xfir.accesso_portale_conservazione',
  'dati_conservazione_xfir.notifiche_avvenuta_conservazione',
  'dati_conservazione_xfir.conservazione_anni_precedenti',
  'comunicazione_itl',
]);

/** Fatture Elettroniche: tipologia_intermediario_sdi */
const TIPOLOGIA_INTERMEDIARIO_SDI: Record<string, string> = {
  professionista: 'Professionista abilitato (commercialista, consulente del lavoro, ecc)',
  software_house: 'Software house e provider di servizi di fatturazione elettronica',
  caf: 'Centro di assistenza fiscale (CAF)',
  associazione_sindacale: 'Associazioni sindacali di categoria tra imprenditori',
  altro: 'Altro',
};

/** Fatture Elettroniche: modalita_trasmissione */
const MODALITA_TRASMISSIONE: Record<string, string> = {
  servizio_online: 'Servizio online presente nel portale "Fatture e Corrispettivi"',
  pec: 'PEC (Posta Elettronica Certificata)',
  canale_accreditato: 'Canale telematico accreditato',
  intermediario: 'Tramite intermediario/provider',
};

/** Fatture Cartacee / Libri Contabili / LUL: modalita_conservazione */
const MODALITA_CONSERVAZIONE: Record<string, string> = {
  cartaceo: 'Cartaceo',
  cartacea: 'Cartacea',
  conservazione_sostitutiva: 'Conservazione Sostitutiva',
  stampa_annuale: 'Conservazione cartacea (Stampa annuale)',
  stampa_su_necessita: 'Stampa solo in caso di ispezione o in casi di necessità',
};

/** LUL: soggetto_conservatore (multiselect values) */
const SOGGETTO_CONSERVATORE_LUL: Record<string, string> = {
  datore_lavoro: 'Datore di lavoro',
  consulente_lavoro: 'Consulente del Lavoro',
  commercialista: 'Commercialista',
  caf: 'Centri di assistenza fiscale - CAF',
  societa_capogruppo: 'Società capogruppo',
  consorzio: 'Consorzio di società cooperative, per conto della consorziata',
};

/** RENTRI: modalita_tenuta */
const MODALITA_TENUTA_RENTRI: Record<string, string> = {
  propri_gestionali: 'con i propri gestionali',
  servizi_rentri: 'con i servizi di supporto messi a disposizione dal RENTRI',
};

/** RENTRI: modalita_conservazione / modalita_scarico_xfir */
const MODALITA_SISTEMA_RENTRI: Record<string, string> = {
  gestionale_rifiuti: 'Dal gestionale dei rifiuti',
  sistema_rentri: 'Dal sistema Rentri',
};

/** Conservazione: tipologia_credenziali */
const TIPOLOGIA_CREDENZIALI: Record<string, string> = {
  credenziali_nominative: 'Credenziali Nominative',
  credenziali_ufficio: "Credenziali d'Ufficio",
};

/** Conservazione: modalita_invio_conservazione */
const MODALITA_INVIO_CONSERVAZIONE: Record<string, string> = {
  manuale: 'Manuale',
  automatica: 'Automatica',
  semiautomatica: 'Semiautomatica',
  altro: 'Altro',
};

/** Conservazione: sistemi_consultazione (multiselect) */
const SISTEMI_CONSULTAZIONE: Record<string, string> = {
  sistema_gestione_documentale: 'Sistema di gestione documentale',
  portale_conservazione_digitale: 'Portale di conservazione digitale',
  ecm: 'ECM - Enterprise Content Management',
  gestionale_presenze_paghe: 'Applicativo gestionale presenze/paghe',
  gestionale_contabilita: 'Applicativo gestionale di contabilità',
  software_fatturazione: 'Applicativo software per la fatturazione',
  portale_fatture_elettroniche: 'Portale invio/ricezione delle fatture elettroniche',
  altro: 'Altro',
  nessuno: 'Nessuno',
};

/** Conservazione: periodicita_conservazione */
const PERIODICITA_CONSERVAZIONE: Record<string, string> = {
  giornaliera: 'Giornaliera',
  mensile: 'Mensile',
  annuale: 'Annuale',
  altro: 'Altro',
};

/** Conservazione: periodicita_chiusura */
const PERIODICITA_CHIUSURA: Record<string, string> = {
  mensile: 'Mensile',
  semestrale: 'Semestrale',
  annuale: 'Annuale',
  altro: 'Altro',
};

/** Conservazione: modalita_generazione_pacchetti_distribuzione */
const MODALITA_GENERAZIONE_PDI: Record<string, string> = {
  portale_conservazione: 'Richiesta/generazione dal Portale di conservazione digitale',
  gestionale: 'Richiesta/generazione da gestionale',
  mail_pec: 'Richiesta con Mail/Pec al conservatore',
  altro: 'Altro',
};

/** PEC: regole_conservazione_pec */
const REGOLE_CONSERVAZIONE_PEC: Record<string, string> = {
  tutti_messaggi: 'Conservare tutti i messaggi (inviati, ricevuti e ricevute)',
  entrata_ricevute: 'Conservare messaggi in entrata e ricevute',
  inviati_ricevuti: 'Conservare tutti i messaggi inviati e ricevuti',
  solo_ricevuti: 'Conservare solo i messaggi ricevuti',
};

/** RENTRI: modalita_conservazione (top-level, used for the "Da quale sistema..." question) */
const MODALITA_CONSERVAZIONE_RENTRI: Record<string, string> = {
  ...MODALITA_SISTEMA_RENTRI,
};

/**
 * Document-type-specific overrides for fields with the same name but different values.
 */
const DOCUMENT_TYPE_VALUE_MAP: Record<string, Record<string, Record<string, string>>> = {
  doc_rentri: {
    modalita_conservazione: MODALITA_SISTEMA_RENTRI,
    'dati_conservazione.modalita_conservazione': MODALITA_SISTEMA_RENTRI,
  },
};

/**
 * Map from field_name (as sent by the backend) → value map.
 * Supports both nested (dati_conservazione.xxx) and flat field names.
 */
const FIELD_VALUE_MAP: Record<string, Record<string, string>> = {
  // Fatture Elettroniche
  tipologia_intermediario_sdi: TIPOLOGIA_INTERMEDIARIO_SDI,
  modalita_trasmissione: MODALITA_TRASMISSIONE,

  // Fatture Cartacee / Libri Contabili / LUL
  modalita_conservazione: MODALITA_CONSERVAZIONE,

  // LUL
  soggetto_conservatore: SOGGETTO_CONSERVATORE_LUL,

  // RENTRI
  obbligo_rentri: BOOLEAN_LABELS,
  modalita_tenuta: MODALITA_TENUTA_RENTRI,
  modalita_scarico_xfir: MODALITA_SISTEMA_RENTRI,

  // Conservazione nested fields
  'dati_conservazione.tipologia_credenziali': TIPOLOGIA_CREDENZIALI,
  'dati_conservazione.modalita_invio_conservazione': MODALITA_INVIO_CONSERVAZIONE,
  'dati_conservazione.sistemi_consultazione': SISTEMI_CONSULTAZIONE,
  'dati_conservazione.periodicita_conservazione': PERIODICITA_CONSERVAZIONE,
  'dati_conservazione.periodicita_chiusura': PERIODICITA_CHIUSURA,
  'dati_conservazione.modalita_generazione_pacchetti_distribuzione': MODALITA_GENERAZIONE_PDI,
  'dati_conservazione.regole_conservazione_pec': REGOLE_CONSERVAZIONE_PEC,
  'dati_conservazione.modalita_conservazione': MODALITA_CONSERVAZIONE,

  // XFIR conservation fields
  'dati_conservazione_xfir.tipologia_credenziali': TIPOLOGIA_CREDENZIALI,
  'dati_conservazione_xfir.modalita_invio_conservazione_xfir': MODALITA_INVIO_CONSERVAZIONE,
  'dati_conservazione_xfir.sistemi_consultazione': SISTEMI_CONSULTAZIONE,
  'dati_conservazione_xfir.periodicita_conservazione_xfir': PERIODICITA_CONSERVAZIONE,
  'dati_conservazione_xfir.periodicita_chiusura_xfir': PERIODICITA_CHIUSURA,
  'dati_conservazione_xfir.modalita_generazione_pacchetti_distribuzione': MODALITA_GENERAZIONE_PDI,
  'dati_conservazione_xfir.modalita_conservazione': MODALITA_CONSERVAZIONE_RENTRI,
};

/**
 * Resolves a backend value to its human-readable label for the given field.
 * Returns the original value if no mapping is found.
 */
export function mapComparisonValue(
  fieldName: string,
  value: string,
  documentType?: string,
): string {
  if (!value) return value;

  if (BOOLEAN_FIELDS.has(fieldName)) {
    return BOOLEAN_LABELS[value.toLowerCase()] ?? value;
  }

  // Check document-type-specific overrides first
  if (documentType) {
    const scopedMap = DOCUMENT_TYPE_VALUE_MAP[documentType]?.[fieldName];
    if (scopedMap) {
      if (value.includes(',')) {
        return value
          .split(',')
          .map((v) => scopedMap[v.trim()] ?? v.trim())
          .join(', ');
      }
      return scopedMap[value] ?? value;
    }
  }

  const valueMap = FIELD_VALUE_MAP[fieldName];
  if (valueMap) {
    // Handle multiselect values (comma-separated)
    if (value.includes(',')) {
      return value
        .split(',')
        .map((v) => valueMap[v.trim()] ?? v.trim())
        .join(', ');
    }
    return valueMap[value] ?? value;
  }

  // Try extracting the leaf field name for flat keys not explicitly mapped
  const leafKey = fieldName.includes('.') ? fieldName.split('.').pop()! : fieldName;
  if (leafKey !== fieldName) {
    const leafMap = FIELD_VALUE_MAP[leafKey];
    if (leafMap) {
      if (value.includes(',')) {
        return value
          .split(',')
          .map((v) => leafMap[v.trim()] ?? v.trim())
          .join(', ');
      }
      return leafMap[value] ?? value;
    }
  }

  return value;
}
